import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { DailyChallenges } from "./DailyChallenges";
import { 
  Target, 
  Flame, 
  Trophy, 
  Calendar,
  TrendingUp,
  Zap,
  Clock
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChallengesPage = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/challenges/history?days=30`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      data-testid="challenges-page"
      className="space-y-6"
    >
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neon-cyan/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-neon-cyan" />
            </div>
            <div>
              <p className="text-2xl font-orbitron text-white">
                {loading ? "..." : history?.total_challenges || 0}
              </p>
              <p className="text-xs text-slate-400 font-rajdhani">Retos Completados</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neon-yellow/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-neon-yellow" />
            </div>
            <div>
              <p className="text-2xl font-orbitron text-white">
                {loading ? "..." : history?.total_xp || 0}
              </p>
              <p className="text-xs text-slate-400 font-rajdhani">XP Total</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neon-purple/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-neon-purple" />
            </div>
            <div>
              <p className="text-2xl font-orbitron text-white">
                {loading ? "..." : history?.days_active || 0}
              </p>
              <p className="text-xs text-slate-400 font-rajdhani">Días Activos</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-orbitron text-white">
                {loading || !history?.total_challenges ? "0%" : 
                  Math.round((history.total_challenges / (history.days_active * 3 || 1)) * 100) + "%"}
              </p>
              <p className="text-xs text-slate-400 font-rajdhani">Tasa de Éxito</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Main Challenges Component */}
      <DailyChallenges />

      {/* Recent Activity */}
      {!loading && history?.completions?.length > 0 && (
        <GlassCard className="p-6">
          <h3 className="font-orbitron text-lg text-white flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-slate-400" />
            Actividad Reciente
          </h3>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.completions.slice(0, 10).map((completion, i) => (
              <div
                key={`${completion.challenge_id}-${completion.date}`}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Target className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-rajdhani">
                      {completion.challenge_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(completion.completed_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-neon-yellow">
                  <Zap className="w-4 h-4" />
                  <span className="font-bold text-sm">+{completion.xp_earned}</span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </motion.div>
  );
};

export default ChallengesPage;
